import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchHostProfile } from "../../services/clubService";
import { getDefaultHostIdFromEnv } from "../../services/commonService";
import {
  eventMatchesCategoryTab,
  pickAddress,
  pickBannerSlideUrls,
  pickBannerUrl,
  pickEventsFromClub,
  pickLogoUrl,
  pickTitle,
} from "../../utils/clubDetailHelpers";
import { vibesColors as colors } from "../../theme/vibesColors";
import { getCategorySectionTitle } from "../../constants/clubConstants";
import WebsiteLayout from "../../layouts/WebsiteLayout";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";
import MissingClubConfig from "../common/MissingClubConfig";
import ClubHeroBanner from "../club/ClubHeroBanner";
import ClubInfoCard from "../club/ClubInfoCard";
import ClubContactCard from "../club/ClubContactCard";
import EventCategoryTabs from "../club/EventCategoryTabs";
import ClubEventsList from "../club/ClubEventsList";
import BackNavButton from "../common/BackNavButton";
import FavoriteAppUnlockButton from "../common/FavoriteAppUnlockButton";

export default function ClubDetailPageComponent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const hostId = (id || getDefaultHostIdFromEnv()).trim();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("club");

  useEffect(() => {
    if (!hostId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchHostProfile(hostId);
        if (cancelled) return;
        if (!result.ok) {
          setError(result.message);
          setData(null);
          return;
        }
        setData(result.data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hostId]);

  const events = useMemo(() => (data ? pickEventsFromClub(data) : []), [data]);
  const filtered = useMemo(() => events.filter((ev) => eventMatchesCategoryTab(ev, tab)), [events, tab]);
  const sectionTitle = getCategorySectionTitle(tab);

  if (!hostId) {
    return (
      <WebsiteLayout>
        <MissingClubConfig />
      </WebsiteLayout>
    );
  }
  if (loading) {
    return (
      <WebsiteLayout>
        <LoadingSpinner />
      </WebsiteLayout>
    );
  }
  if (error || !data) {
    return (
      <WebsiteLayout>
        <ErrorMessage message={error || "Not found"} />
      </WebsiteLayout>
    );
  }

  const title = pickTitle(data);
  const address = pickAddress(data);
  const logo = pickLogoUrl(data);
  const cover = pickBannerUrl(data);
  const email = data.email || data.contactEmail || "";
  const phone = data.phone || data.mobile || data.contactPhone || "";

  return (
    <WebsiteLayout>
      <ClubHeroBanner
        imageUrls={pickBannerSlideUrls(data)}
        venueName={title}
        logoUrl={logo}
        topLeftNode={
          <BackNavButton
            ariaLabel="Back"
            onClick={() => {
              navigate("/", { replace: true });
            }}
          />
        }
        topRightNode={<FavoriteAppUnlockButton variant="hero" ariaLabel="Favorite venue" />}
      />
      <div className="club-page-body">
        <div className="row g-4 align-items-start">
          <div className="col-12 col-lg-3 col-xl-3">
            <div className="club-sidebar-stack">
              <ClubInfoCard variant="compact" title={title} address={address} logoUrl={logo} />
              <ClubContactCard displayName={title} email={email} phone={phone} />
            </div>
          </div>
          <div className="col-12 col-lg-9 col-xl-9">
            <EventCategoryTabs activeKey={tab} onChange={setTab} />
            <h2 className="h5 mt-4 mb-3" style={{ fontWeight: 700, color: colors.white }}>
              {sectionTitle} ({filtered.length})
            </h2>
            <ClubEventsList events={filtered} coverImageUrl={cover} hostId={hostId} />
          </div>
        </div>
      </div>
    </WebsiteLayout>
  );
}
